// Unified identity platform (documentation/identity-architecture.md, protocol.md "Identity"):
// people, invitations and assignments; the caller's own sign-in methods; the platform security
// configuration; the public invitation link. The server decides every change (delegation policy,
// never yourself, never above your own authority) - the client only offers what /options allows.
import { apiRequest } from './tenancy';

export type PersonStatus = 'invited' | 'active' | 'disabled';
export type SignInMethodType = 'radioos' | 'microsoft' | 'google';
/** Invitation / mail delivery. "sent" = accepted by the mail service for delivery (not proof of receipt). */
export type DeliveryState = 'pending' | 'sending' | 'sent' | 'failed' | 'not_configured' | 'cancelled';

export interface NamedRef {
  id: string;
  name: string;
}

export interface AssignmentView {
  id: string;
  role: string;
  role_label: string;
  oem_partner_id?: string;
  oem_partner_name?: string;
  customer_id?: string;
  customer_name?: string;
  /** A legacy single-department grant. */
  department_id?: string;
  department_restricted: boolean;
  departments: NamedRef[];
  capabilities: string[];
  manageable: boolean;
  created_at: string;
}

export interface Person {
  id: string;
  email: string;
  display_name?: string;
  status: PersonStatus;
  /** A legacy network/bootstrap account (managed on the network Users page). */
  legacy: boolean;
  assignments: AssignmentView[];
  methods: SignInMethodType[];
  mfa_enrolled: boolean;
  invitation?: { expires_at: string; expired: boolean; delivery_state: DeliveryState; invited_by?: string };
  last_login_at?: string;
  created_at: string;
  self: boolean;
  manageable: boolean;
  /** The caller may delete this person (Delete person); otherwise delete_blocked is the reason code. */
  deletable?: boolean;
  delete_blocked?: string;
}

/** What a completed Delete person removed (never a credential). */
export interface DeletedPerson {
  id: string;
  email?: string;
  display_name?: string;
  sign_in_methods: SignInMethodType[];
  assignments_removed: string[];
  revoked_sessions: number;
}

export interface AssignmentSpec {
  role: string;
  oem_partner_id?: string;
  customer_id?: string;
  capabilities?: string[];
  department_restricted?: boolean;
  departments?: string[];
}

export interface RoleOption {
  role: string;
  label: string;
  scope: 'platform' | 'oem' | 'customer';
}

export interface IdentityOptions {
  roles: RoleOption[];
  capabilities: string[];
  oem_partners: { id: string; name: string; status: string; roles: string[] }[];
  customers: { id: string; name: string; oem_partner_id?: string; departments: NamedRef[]; roles: string[] }[];
  can_invite: boolean;
}

export interface IssuedInvitation {
  invitation: { id: string; user_id: string; email: string; expires_at: string; delivery_state: DeliveryState };
  delivery_state: DeliveryState;
  /** Present only when the e-mail was NOT sent: shown once, never stored by the browser. */
  invitation_url?: string;
  notice: string;
}

const seg = encodeURIComponent;
const json = (method: string, body?: unknown): RequestInit => ({ method, body: body === undefined ? undefined : JSON.stringify(body) });

export const listPeople = () => apiRequest<{ users: Person[] }>('/api/identity/users').then((r) => r.users || []);
export const getIdentityOptions = () => apiRequest<IdentityOptions>('/api/identity/options');
export const invitePerson = (email: string, displayName: string, assignments: AssignmentSpec[]) =>
  apiRequest<IssuedInvitation>('/api/identity/invitations', json('POST', { email, display_name: displayName, assignments }));
export const reissueInvitation = (userId: string) => apiRequest<IssuedInvitation>(`/api/identity/users/${seg(userId)}/invitation`, json('POST'));
export const cancelInvitation = (userId: string) => apiRequest<void>(`/api/identity/users/${seg(userId)}/invitation`, { method: 'DELETE' });
export const addAssignment = (userId: string, spec: AssignmentSpec) => apiRequest<{ id: string }>(`/api/identity/users/${seg(userId)}/assignments`, json('POST', spec));
export const updateAssignment = (id: string, spec: Pick<AssignmentSpec, 'capabilities' | 'department_restricted' | 'departments'>) =>
  apiRequest<void>(`/api/identity/assignments/${seg(id)}`, json('PUT', spec));
export const removeAssignment = (id: string) => apiRequest<void>(`/api/identity/assignments/${seg(id)}`, { method: 'DELETE' });
export const setPersonDisabled = (userId: string, disabled: boolean) =>
  apiRequest<void>(`/api/identity/users/${seg(userId)}/${disabled ? 'disable' : 'enable'}`, json('POST'));
/** Permanently deletes a person's RadioOS identity; confirm = the person's e-mail address. */
export const deletePerson = (userId: string, confirm: string) =>
  apiRequest<{ status: 'deleted'; person: DeletedPerson }>(`/api/identity/users/${seg(userId)}`, json('DELETE', { confirm }));

// --- own sign-in methods ---------------------------------------------------------------------------

export interface SignInMethod {
  id?: string;
  type: SignInMethodType;
  account?: string;
  mfa_enrolled?: boolean;
  password_set?: boolean;
  linked_at: string;
  last_login_at?: string;
}

export interface MyMethods {
  methods: SignInMethod[];
  linkable: ('microsoft' | 'google')[];
  /** The person has a RadioOS authenticator app (TOTP). */
  authenticator?: boolean;
  /** It can be set up here for a Microsoft/Google sign-in. */
  authenticator_setup_available?: boolean;
}

export const getMyMethods = () => apiRequest<MyMethods>('/api/account/sign-in-methods');
export const beginLink = (provider: 'microsoft' | 'google') =>
  apiRequest<{ redirect_url: string }>(`/api/account/sign-in-methods/${seg(provider)}/link`, json('POST'));
export const unlinkMethod = (id: string) => apiRequest<void>(`/api/account/sign-in-methods/${seg(id)}`, { method: 'DELETE' });
/** Opens the authenticator enrolment for the current Microsoft/Google sign-in (then /api/auth/mfa/enroll/*). */
export const setUpAuthenticator = () =>
  apiRequest<{ challenge: string; next: 'enroll' | 'verify'; expires_in: number }>('/api/account/authenticator/setup', json('POST'));

// --- platform security configuration -----------------------------------------------------------------

export interface ProviderSetting {
  provider: 'radioos' | 'microsoft' | 'google';
  enabled: boolean;
  /** SECURITY OVERRIDE: every sign-in of this provider counts as MFA without evidence. */
  mfa_override: boolean;
  configured: boolean;
}

export interface MFASubject {
  scope_type: 'platform' | 'oem' | 'customer';
  scope_id?: string;
  scope_name?: string;
  subject: string;
  requirement: 'required' | 'optional';
  mandatory?: boolean;
}

export interface DelegationRule {
  actor_role: string;
  target_role: string;
  allowed: boolean;
}

export interface MailSettings {
  provider: 'none' | 'graph';
  enabled: boolean;
  graph_tenant_id: string;
  graph_client_id: string;
  sender_address: string;
  sender_name: string;
  /** Show a not-e-mailed invitation / reset link once to the issuing administrator. */
  link_fallback: boolean;
  security_notifications: boolean;
}

/** Settings -> Configuration -> E-mail (never contains the credential). */
export interface MailStatus extends MailSettings {
  state: 'not_configured' | 'configured' | 'error';
  credential_configured: boolean;
  credential_source: 'external-file' | 'environment' | 'none';
  credential_error?: string;
  ready: boolean;
  last_test_at?: string;
  last_test_result?: string;
  last_failure_category?: string;
  last_failure_at?: string;
  last_sent_at?: string;
  secret_installation: string;
  /** client_secret | certificate; for a certificate only PUBLIC facts. */
  credential_type?: 'client_secret' | 'certificate';
  credential_thumbprint?: string;
  credential_not_after?: string;
  credential_installed_at?: string;
  /** Automated rotation (server timer; documentation/mail-credential-rotation.md). */
  rotation?: MailRotation;
}

export interface MailRotation {
  health: 'ok' | 'warning' | 'critical' | 'unknown';
  reasons: string[];
  expires_in_days?: number;
  meta: { state: string; error?: string };
  report?: {
    type: 'certificate' | 'client_secret';
    thumbprint?: string;
    not_after?: string;
    key_id?: string;
    rotation?: {
      scheduler: 'systemd-timer' | 'none';
      enabled: boolean;
      lifetime_days?: number;
      rotate_before_days?: number;
      next_check_at?: string;
      due_at?: string;
      last_attempt_at?: string;
      last_result?: 'success' | 'failed' | 'halted';
      last_success_at?: string;
      last_failure_step?: string;
      failures: number;
      pending_removals: number;
    };
  };
}

export interface MailTestResult {
  delivery_state: DeliveryState;
  to?: string;
  delivery_category?: string;
  hint?: string;
}

export const getProviders = () => apiRequest<{ providers: ProviderSetting[]; reachable_platform_admins: number }>('/api/admin/identity/providers');
export const setProvider = (provider: string, enabled: boolean, mfaOverride: boolean) =>
  apiRequest<void>(`/api/admin/identity/providers/${seg(provider)}`, json('PUT', { enabled, mfa_override: mfaOverride }));
export const getMFAPolicy = () => apiRequest<{ policies: MFASubject[]; roles: string[]; capabilities: string[] }>('/api/admin/identity/mfa-policy');
export const setMFAPolicy = (s: MFASubject) => apiRequest<void>('/api/admin/identity/mfa-policy', json('PUT', s));
export const getDelegation = () => apiRequest<{ rules: DelegationRule[] }>('/api/admin/identity/delegation').then((r) => r.rules || []);
export const setDelegation = (r: DelegationRule) => apiRequest<void>('/api/admin/identity/delegation', json('PUT', r));
export const getMail = () => apiRequest<MailStatus>('/api/admin/identity/mail');
export const setMail = (m: MailSettings) => apiRequest<void>('/api/admin/identity/mail', json('PUT', m));
/** Sends the test message to the administrator's own address (the server refuses any other). */
export const testMail = (to?: string) => apiRequest<MailTestResult>('/api/admin/identity/mail/test', json('POST', to ? { to } : {}));

// --- the public invitation link (no session) -----------------------------------------------------------

type Fetcher = (input: string, init?: RequestInit) => Promise<Response>;
const publicPost = (fetcher: Fetcher, path: string, body: unknown) =>
  fetcher(path, { method: 'POST', credentials: 'same-origin', cache: 'no-store', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });

export interface InvitationInfo {
  email: string;
  display_name?: string;
  invited_by?: string;
  expires_at: string;
  roles: string[];
  methods: SignInMethodType[];
}

export type InvitationCheck = { kind: 'valid'; invitation: InvitationInfo } | { kind: 'invalid' } | { kind: 'unavailable' };

/** Checks an invitation link without using it. */
export async function inspectInvitation(token: string, fetcher: Fetcher = (i, n) => fetch(i, n)): Promise<InvitationCheck> {
  try {
    const res = await publicPost(fetcher, '/api/invitations/inspect', { token });
    if (res.ok) {
      const body = (await res.json()) as { invitation?: InvitationInfo };
      return body.invitation ? { kind: 'valid', invitation: body.invitation } : { kind: 'unavailable' };
    }
    if (res.status === 410 || res.status === 404) return { kind: 'invalid' };
    return { kind: 'unavailable' };
  } catch {
    return { kind: 'unavailable' };
  }
}

export type AcceptResult = { kind: 'redirect'; url: string } | { kind: 'invalid' } | { kind: 'unavailable' } | { kind: 'refused'; message: string };

/**
 * Accepts with a method: Microsoft/Google return the provider sign-in to open (the server binds the
 * verified account in its callback); RadioOS returns the one-time activation page for this browser.
 */
export async function acceptInvitation(token: string, method: SignInMethodType, fetcher: Fetcher = (i, n) => fetch(i, n)): Promise<AcceptResult> {
  try {
    const res = await publicPost(fetcher, '/api/invitations/accept', { token, method });
    const body = (await res.json().catch(() => ({}))) as { redirect_url?: string; message?: string };
    if (res.ok) return body.redirect_url ? { kind: 'redirect', url: body.redirect_url } : { kind: 'unavailable' };
    if (res.status === 410 || res.status === 404) return { kind: 'invalid' };
    if (res.status === 409) return { kind: 'refused', message: body.message || 'The invitation could not be accepted this way.' };
    return { kind: 'unavailable' };
  } catch {
    return { kind: 'unavailable' };
  }
}
